import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Star, Sparkles } from 'lucide-react-native';

type Variant = 'light' | 'dark' | 'warm';

interface ChildFriendlyBackgroundProps {
    variant?: Variant;
    children: React.ReactNode;
    scrollable?: boolean;
}

const variantConfig: Record<Variant, {
    backgroundColor: string;
    blobs: string[];
    sparkles: { color: string; fill?: string }[];
    dots: string[];
}> = {
    light: {
        backgroundColor: '#E6F7FF',
        blobs: ['#BAE7FF', '#FFD8BF', '#D9F7BE', '#FFCCC7'],
        sparkles: [
            { color: '#FAAD14', fill: '#FAAD14' },
            { color: '#52C41A' },
            { color: '#1890FF', fill: '#1890FF' },
            { color: '#FA8C16' },
            { color: '#EB2F96', fill: '#EB2F96' },
        ],
        dots: ['#52C41A', '#FAAD14', '#1890FF', '#EB2F96'],
    },
    dark: {
        backgroundColor: '#0B1B3D',
        blobs: ['#1E3A8A', '#172554', '#1D4ED8', '#2563EB'],
        sparkles: [
            { color: '#FCD34D', fill: '#FCD34D' },
            { color: '#60A5FA' },
            { color: '#34D399', fill: '#34D399' },
            { color: '#FBBF24' },
            { color: '#A78BFA', fill: '#A78BFA' },
        ],
        dots: ['#60A5FA', '#FCD34D', '#34D399', '#A78BFA'],
    },
    warm: {
        backgroundColor: '#FFF7E6',
        blobs: ['#FFD8BF', '#FFCCC7', '#FFE7BA', '#FFF1B8'],
        sparkles: [
            { color: '#FAAD14', fill: '#FAAD14' },
            { color: '#FA541C' },
            { color: '#EB2F96', fill: '#EB2F96' },
            { color: '#FA8C16' },
            { color: '#52C41A', fill: '#52C41A' },
        ],
        dots: ['#FAAD14', '#FA541C', '#EB2F96', '#52C41A'],
    },
};

export function ChildFriendlyBackground({ variant = 'light', children, scrollable = true }: ChildFriendlyBackgroundProps) {
    const config = variantConfig[variant];

    return (
        <SafeAreaView style={[styles.container, { backgroundColor: config.backgroundColor }]}>
            {/* Background blobs */}
            <View style={[styles.blob, styles.blobTopRight, { backgroundColor: config.blobs[0] }]} />
            <View style={[styles.blob, styles.blobMidLeft, { backgroundColor: config.blobs[1] }]} />
            <View style={[styles.blob, styles.blobBottomRight, { backgroundColor: config.blobs[2] }]} />
            <View style={[styles.blob, styles.blobTopLeft, { backgroundColor: config.blobs[3] }]} />

            {/* Floating sparkle decorations */}
            <View style={[styles.sparkle, { top: 80, left: 40 }]}>
                <Star size={16} color={config.sparkles[0].color} fill={config.sparkles[0].fill} />
            </View>
            <View style={[styles.sparkle, { top: 140, right: 60 }]}>
                <Sparkles size={20} color={config.sparkles[1].color} />
            </View>
            <View style={[styles.sparkle, { top: 320, right: 30 }]}>
                <Star size={14} color={config.sparkles[2].color} fill={config.sparkles[2].fill} />
            </View>
            <View style={[styles.sparkle, { bottom: 200, left: 50 }]}>
                <Sparkles size={18} color={config.sparkles[3].color} />
            </View>
            <View style={[styles.sparkle, { bottom: 120, right: 80 }]}>
                <Star size={12} color={config.sparkles[4].color} fill={config.sparkles[4].fill} />
            </View>

            {/* Floating dots */}
            <View style={[styles.dot, styles.dot1, { backgroundColor: config.dots[0] }]} />
            <View style={[styles.dot, styles.dot2, { backgroundColor: config.dots[1] }]} />
            <View style={[styles.dot, styles.dot3, { backgroundColor: config.dots[2] }]} />
            <View style={[styles.dot, styles.dot4, { backgroundColor: config.dots[3] }]} />

            {scrollable ? (
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    bounces={false}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                >
                    {children}
                </ScrollView>
            ) : (
                <View style={styles.content}>
                    {children}
                </View>
            )}
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    blob: {
        position: 'absolute',
        borderRadius: 999,
    },
    blobTopRight: {
        width: 180,
        height: 180,
        top: 20,
        right: -50,
        opacity: 0.6,
    },
    blobMidLeft: {
        width: 140,
        height: 140,
        top: 260,
        left: -40,
        opacity: 0.5,
    },
    blobBottomRight: {
        width: 220,
        height: 220,
        bottom: 40,
        right: -70,
        opacity: 0.55,
    },
    blobTopLeft: {
        width: 100,
        height: 100,
        top: 100,
        left: -20,
        opacity: 0.45,
    },
    sparkle: {
        position: 'absolute',
        opacity: 0.8,
    },
    dot: {
        position: 'absolute',
        width: 10,
        height: 10,
        borderRadius: 5,
        opacity: 0.5,
    },
    dot1: {
        top: 180,
        left: 80,
    },
    dot2: {
        top: 240,
        right: 100,
        width: 14,
        height: 14,
        borderRadius: 7,
    },
    dot3: {
        bottom: 280,
        right: 40,
    },
    dot4: {
        bottom: 160,
        left: 100,
        width: 8,
        height: 8,
        borderRadius: 4,
    },
    content: {
        flex: 1,
    },
    scrollView: {
        flex: 1,
        width: '100%',
    },
    scrollContent: {
        flexGrow: 1,
    },
});
